import React, { ReactNode, useMemo } from "react";
import { UserPaymentTableEntry } from "../../apis/armoryApi";
import BaseDataTable, { ColumnMeta } from "./BaseDataTable";

interface UserPaymentDataTableProps {
  isLoading: boolean;
  userPaymentsData: UserPaymentTableEntry[];
  onRefreshButtonClick: () => void;
  columnOverrides?: ColumnMeta<UserPaymentTableEntry>[];
  additionalActions?: ReactNode;
}

const UserPaymentDataTable: React.FC<UserPaymentDataTableProps> = ({
  isLoading,
  userPaymentsData,
  onRefreshButtonClick,
  columnOverrides = [],
  additionalActions,
}) => {
  const defaultColumns: ColumnMeta<UserPaymentTableEntry>[] = [
    {
      header: "ID",
      field: "id",
      filter: true,
      filterPlaceholder: "Search by Payment ID",
      isSortable: true,
      isVisible: false,
      isCopyable: true,
    },
    {
      header: "Inventory Object",
      field: "inventoryObjectDesc",
      filter: true,
      filterPlaceholder: "Search by Inventory Object",
      isSortable: true,
      isVisible: true,
      isCopyable: true,
    },
    {
      header: "Payment Date",
      field: "paymentDate",
      filter: true,
      filterPlaceholder: "Search by Date",
      isSortable: true,
      isVisible: true,
      isCopyable: true,
    },
    {
      header: "Payer",
      field: "payerName",
      filter: true,
      filterPlaceholder: "Search by Payer",
      isSortable: true,
      isVisible: true,
      isCopyable: true,
    },
    {
      header: "Amount",
      field: "amount",
      body: (row) => `$${row.amount.toFixed(2)}`,
      filter: true,
      filterPlaceholder: "Search by Amount",
      isSortable: true,
      isVisible: true,
      isCopyable: true,
    },
    {
      header: "Receiver",
      field: "receivedByName",
      filter: true,
      filterPlaceholder: "Search by Receiver",
      isSortable: true,
      isVisible: true,
      isCopyable: true,
    },
    {
      header: "Notes",
      field: "notes",
      filter: true,
      filterPlaceholder: "Filter Notes",
      isSortable: true,
      isVisible: true,
      isCopyable: true,
    },
    {
      header: "Deleted",
      field: "isDeleted",
      body: (row) => (row.isDeleted ? "Yes" : "No"),
      filter: true,
      filterPlaceholder: "Filter Deleted",
      isSortable: true,
      isVisible: true,
      isCopyable: false,
    },
  ];

  const columns = useMemo(() => {
    return defaultColumns.map((col) => {
      const override = columnOverrides.find((o) => o.field === col.field);
      return override ? { ...col, ...override } : col;
    });
  }, [columnOverrides]);

  return (
    <BaseDataTable
      isLoading={isLoading}
      data={userPaymentsData}
      columns={columns}
      onRefreshButtonClick={onRefreshButtonClick}
      additionalActions={additionalActions}
    />
  );
};

export default UserPaymentDataTable;